import React, { useContext } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { buildLink, getPath } from "common/routing";
import {
  GetWorkflowDefinitionQuery,
  useGetWorkflowDefinitionQuery,
} from "../../../generated/graphql";
import { spacing } from "../../../stitches.config";
import { setTitle } from "../../../utils/title";
import { getCurrentURL } from "../../../utils/url";
import { PublicUserContext } from "../../Authentication/PublicUserContext";
import usePublicUserLoginModal from "../../Authentication/PublicUserLoginModal";
import { AuthContext } from "../../Authorization/AuthContext";
import { FlexColumn } from "../../Common/__styles__/Layout";
import { MarkdownContainer } from "../../Common/__styles__/MarkdownContainer";
import { Button } from "../../Common/Button";
import { Title } from "../../Common/Typography";
import { Loading } from "../__styles__/Guest";
import AccessDeniedPage, { ACCESS_DENIED_RESOURCE } from "../AccessDeniedPage";
import Layout from "../Layout";
import { accountTitle } from "../utils";

export const WorkflowDefinition = () => {
  const history = useHistory();
  const location = useLocation<{ prevLocation?: string }>();
  const { publicUser } = useContext(AuthContext);
  const { workflowDefinitionId } = useParams<{
    workflowDefinitionId: string;
  }>();

  const { data, loading } = useGetWorkflowDefinitionQuery({
    variables: {
      workflowDefinitionId,
      isGuest: !publicUser,
    },
    fetchPolicy: "network-only",
  });

  const title = data?.workflowDefinition?.name ?? "Loading...";
  setTitle(`${title} | ${accountTitle()}`);

  if (loading) {
    return <Loading />;
  }

  if (!data?.workflowDefinition) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.WORKFLOW} />;
  }

  const prevLocation = location.state?.prevLocation ?? getPath("guestHome");

  return (
    <Layout hasGutters={true}>
      <FlexColumn
        style={{ margin: `${spacing.l.value} 0`, gap: spacing.l.value }}
      >
        <Button
          styleVariant="buttonLink"
          leftIconName="chevron-left"
          iconSpacing="xs"
          onClick={() => history.push(prevLocation)}
          fitContent
        >
          Back
        </Button>
        <Title size="large" type="semiBold">
          {data.workflowDefinition.name}
        </Title>

        {data.workflowDefinition.description && (
          <MarkdownContainer markdown={data.workflowDefinition.description} />
        )}
        <CallToAction
          workflowDefinition={data.workflowDefinition}
          workflows={data.workflows ?? []}
        />
      </FlexColumn>
    </Layout>
  );
};

const CallToAction = ({
  workflowDefinition,
  workflows,
}: {
  workflowDefinition: NonNullable<
    GetWorkflowDefinitionQuery["workflowDefinition"]
  >;
  workflows: NonNullable<GetWorkflowDefinitionQuery["workflows"]>;
}) => {
  const { publicUser } = useContext(AuthContext);
  const { refetchCurrentPublicUser } = useContext(PublicUserContext);
  const history = useHistory();
  const location = useLocation();

  const callToActionDisplay = workflows?.length
    ? "View workflows"
    : workflowDefinition.callToAction;

  const [showPublicUserLoginModal] = usePublicUserLoginModal({
    onLoginSuccess: () => {
      void refetchCurrentPublicUser();
    },
  });

  const canCreateGroups = workflowDefinition.groups.map(({ id }) => id);
  let onCallToActionClick: () => void;
  if (!publicUser) {
    onCallToActionClick = showPublicUserLoginModal;
  } else if (workflows?.length) {
    onCallToActionClick = () => {
      history.push(
        buildLink("listWorkflows", {
          workflowDefinitionId: workflowDefinition.id,
        }),
        { prevLocation: getCurrentURL(location) }
      );
    };
  } else if (publicUser.groups.some(({ id }) => canCreateGroups.includes(id))) {
    onCallToActionClick = () => {
      history.push(
        buildLink("createSubmission", {
          submissionTypeId: workflowDefinition.initialSubmissionTypeId,
        }),
        { prevLocation: getCurrentURL(location) }
      );
    };
  } else {
    return null;
  }

  return (
    <Button
      styleVariant="primary"
      size="medium"
      fitContent
      onClick={onCallToActionClick}
    >
      {callToActionDisplay}
    </Button>
  );
};
